import { CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

// Shared type used across intake modules
import type { IntakeStatuses } from '@/hooks/useIntakeForm';
import { useIntakeRetainer } from '@/hooks/useIntakeRetainer';
import {
  useSubjectDocumentsListQuery,
  useSubjectDocumentsResendMutation,
} from '@/services/api/documentSubjectService';
import { useVoidLatestLoeMutation } from '@/services/api/intakeService';

import { Button } from '../../ui/button';
import { Field, Label } from '../../ui/catalyst/fieldset';
import { Select } from '../../ui/catalyst/select';
import { Textarea } from '../../ui/catalyst/textarea';
import type { IntakeFormData as IntakeData } from '../form/types';
import { isSignedLoe } from '../utils/loeStatusHelpers';

const formatDocumentStatus = (status: string): string => {
  switch (status) {
    case 'CREATED':
      return 'Created';
    case 'SENT':
      return 'Sent';
    case 'SIGNED':
      return 'Signed';
    case 'VOIDED':
      return 'Voided';
    default:
      return status
        .toLowerCase()
        .split('_')
        .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
        .join(' ');
  }
};

interface StatusTrackingPanelProps {
  intakeId: string;
  statuses: IntakeStatuses;
  onStatusChange: (statuses: IntakeStatuses) => void;
  clientContactInfo: {
    phone?: string;
  };
  onSendEngagementLetter: (options?: { cancelPrevious?: boolean }) => void;
  intakeData: IntakeData;
  onSendSuccess: () => void;
  onSave: () => Promise<void>;
}

const StatusTrackingPanel: React.FC<StatusTrackingPanelProps> = ({
  intakeId,
  statuses,
  onStatusChange,
  clientContactInfo,
  onSendEngagementLetter,
  intakeData,
  onSendSuccess,
  onSave,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const {
    data: intakeDocuments = [],
    isFetching: isIntakeDocumentsFetching,
    isLoading: isIntakeDocumentsLoading,
    refetch: refetchIntakeDocuments,
  } = useSubjectDocumentsListQuery({
    subjectId: intakeId,
    subjectType: 'intake',
  });
  const { latestEngagementLetter: latestLoe } = useIntakeRetainer({ intakeId });

  const [resendLoeReminder, { isLoading: isResendingLoe }] =
    useSubjectDocumentsResendMutation();

  const [voidLatestLoe, { isLoading: isVoidingLoe }] =
    useVoidLatestLoeMutation();

  const showLoeSummary = Boolean(intakeId && latestLoe);
  const hasManualOverride = statuses.loeStatus.includes('(Manual)');
  const allowAutomatedActions = showLoeSummary && !hasManualOverride;
  const latestLoeRequiresSignatures = Boolean(
    latestLoe?.requires_signatures ??
      latestLoe?.document_template?.requires_signatures,
  );
  const canResendReminder = Boolean(
    allowAutomatedActions &&
      latestLoeRequiresSignatures &&
      latestLoe?.status === 'SENT',
  );
  const canVoidLatestLoe = Boolean(
    allowAutomatedActions &&
      latestLoeRequiresSignatures &&
      (latestLoe?.status === 'CREATED' || latestLoe?.status === 'SENT'),
  );
  const latestLoeTimestamp = useMemo(() => {
    if (!latestLoe?.created_at) {
      return null;
    }
    const parsed = new Date(latestLoe.created_at);
    return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString();
  }, [latestLoe?.created_at]);
  const isMatterDocumentsBusy =
    isIntakeDocumentsLoading || isIntakeDocumentsFetching;
  const disableDocActions =
    isMatterDocumentsBusy || isResendingLoe || isVoidingLoe;
  const showCreateNew = allowAutomatedActions; // available when we can act on LOE
  const showAnyActions =
    allowAutomatedActions &&
    (canResendReminder || canVoidLatestLoe || showCreateNew);

  const extractErrorMessage = (error: unknown, fallback: string): string => {
    if (error && typeof error === 'object') {
      const err = error as Record<string, any>;
      if (err.data) {
        if (typeof err.data === 'string') {
          return err.data;
        }
        if (
          typeof err.data === 'object' &&
          err.data !== null &&
          typeof err.data.error === 'string'
        ) {
          return err.data.error;
        }
      }
      if (typeof err.message === 'string' && err.message.trim()) {
        return err.message;
      }
    }
    return fallback;
  };

  const handleResendReminder = async () => {
    if (!intakeId || !latestLoe || !canResendReminder) {
      return;
    }
    try {
      await resendLoeReminder({
        subjectId: intakeId,
        subjectType: 'intake',
        id: latestLoe.id,
        // Backend requires body but doesn't use it for resend action
        documentInstance: {} as Parameters<typeof resendLoeReminder>[0]['documentInstance'],
      }).unwrap();
      toast.success('Reminder sent');
      await refetchIntakeDocuments();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to resend LOE reminder'));
    }
  };

  const handleVoidLatestLoe = async () => {
    if (!intakeId || !latestLoe || !canVoidLatestLoe) {
      return;
    }
    const confirmed = window.confirm(
      'Are you sure you want to void the latest LOE? This cannot be undone.',
    );
    if (!confirmed) {
      return;
    }
    try {
      await voidLatestLoe(intakeId).unwrap();
      toast.success('LOE voided');
      await refetchIntakeDocuments();
      onSendSuccess();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to void LOE'));
    }
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleStatusChange = (
    field: keyof IntakeStatuses,
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    onStatusChange({
      ...statuses,
      [field]: event.target.value as any,
    });
  };

  const handleRejectionReasonChange = (reason: string) => {
    onStatusChange({
      ...statuses,
      rejectionReason: reason,
    });
  };

  const handleSendLOE = () => {
    // If any LOE exists (e.g., recently voided), default to creating a new one
    // to avoid backend multiple-instance conflicts.
    onSendEngagementLetter?.({ cancelPrevious: Boolean(latestLoe) });
  };

  const handleCreateNewLoe = () => {
    onSendEngagementLetter?.({ cancelPrevious: true });
  };

  const handleMarkSentManual = () => {
    onStatusChange({
      ...statuses,
      loeStatus: 'Sent (Manual)',
    });
  };

  const handleMarkSignedManual = () => {
    onStatusChange({
      ...statuses,
      loeStatus: 'Signed (Manual)',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Signed':
      case 'Signed (Manual)':
      case 'Approved':
      case 'Received':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'Sent':
      case 'Sent (Manual)':
      case 'Requested':
        return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'Attorney Rejection':
      case 'Finch Rejection':
        return 'text-red-700 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  // Calculate overall progress (now only LOE and Attorney Decision)
  const completedCount = [
    isSignedLoe(statuses.loeStatus),
    statuses.attorneyDecision === 'Attorney Approved',
  ].filter(Boolean).length;
  const totalCount = 2;
  const progressPercentage = (completedCount / totalCount) * 100;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      <Button
        variant="ghost"
        onClick={handleToggle}
        className="w-full justify-between p-3 text-left hover:bg-gray-50"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">
            Intake Progress
          </span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500" />
        )}
      </Button>

      {isExpanded && (
        <div className="px-3 pb-3 border-t border-gray-100">
          <div className="pt-3">
            {/* Overall Progress */}
            <div className="mb-3 p-2.5 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-semibold text-blue-900">
                  Overall Progress
                </span>
                <span className="text-xs font-medium text-blue-700">
                  {completedCount} of {totalCount} completed
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-1">
                <div
                  className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            <div className="space-y-3">
              {/* Section A: LOE (Retainer Status) */}
              <div className="p-2.5 bg-gray-50 border border-gray-200 rounded-md">
                <h4 className="text-xs font-semibold text-gray-800 mb-2 flex items-center">
                  <svg
                    className="w-3.5 h-3.5 mr-1.5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  A. LOE (Retainer Status)
                </h4>
                {statuses.loeStatus === 'Not Sent' ? (
                  <div className="space-y-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs px-3 py-1.5 w-full"
                      onClick={handleSendLOE}
                    >
                      Send LOE
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-xs px-3 py-1.5 w-full"
                      onClick={handleMarkSentManual}
                    >
                      Mark Sent Manually
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Field>
                      <Select
                        value={statuses.loeStatus}
                        onChange={(event) =>
                          handleStatusChange(
                            'loeStatus',
                            event as unknown as React.ChangeEvent<HTMLSelectElement>,
                          )
                        }
                        className="w-full text-xs"
                      >
                        <option value="Sent" disabled>
                          Sent (System)
                        </option>
                        <option value="Signed" disabled>
                          Signed (System)
                        </option>
                        <option value="Sent (Manual)">Sent (Manual)</option>
                        <option value="Signed (Manual)">Signed (Manual)</option>
                      </Select>
                    </Field>
                    {statuses.loeStatus === 'Sent (Manual)' && (
                      <Button
                        variant="secondary"
                        size="sm"
                        className="text-xs px-3 py-1.5 w-full"
                        onClick={handleMarkSignedManual}
                      >
                        Mark Signed Manually
                      </Button>
                    )}
                    {statuses.loeStatus.includes('(Manual)') && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs px-3 py-1.5 w-full"
                        onClick={handleSendLOE}
                      >
                        Send LOE via Finch
                      </Button>
                    )}
                    {showLoeSummary && latestLoe && (
                      <div className="mt-1">
                        {latestLoe.status === 'SENT' && latestLoeTimestamp && (
                          <p className="text-[11px] text-gray-500">
                            Latest Sent: {latestLoeTimestamp}
                          </p>
                        )}
                        {showAnyActions && (
                          <div className="mt-2 grid grid-cols-1 gap-2">
                            {canResendReminder && (
                              <Button
                                variant="default"
                                size="sm"
                                className="w-full"
                                onClick={handleResendReminder}
                                disabled={disableDocActions}
                              >
                                {isResendingLoe
                                  ? 'Sending…'
                                  : 'Resend reminder'}
                              </Button>
                            )}
                            {canVoidLatestLoe && (
                              <Button
                                variant="default"
                                size="sm"
                                className="w-full"
                                onClick={handleVoidLatestLoe}
                                disabled={disableDocActions}
                              >
                                {isVoidingLoe ? 'Voiding…' : 'Void LOE'}
                              </Button>
                            )}
                            {showCreateNew && (
                              <Button
                                variant="default"
                                size="sm"
                                className="w-full"
                                onClick={handleCreateNewLoe}
                                disabled={disableDocActions}
                                title="Cancels in‑flight LOE first when applicable"
                              >
                                Create new LOE
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Section B: Attorney Decision */}
              <div className="p-2.5 bg-orange-50 border border-orange-200 rounded-md">
                <h4 className="text-xs font-semibold text-orange-800 mb-2 flex items-center">
                  <svg
                    className="w-3.5 h-3.5 mr-1.5 text-orange-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  B. Attorney Decision
                </h4>
                <Field>
                  <Select
                    value={statuses.attorneyDecision}
                    onChange={(event) =>
                      handleStatusChange('attorneyDecision', event)
                    }
                    className="w-full text-xs"
                  >
                    <option value="Not Reviewed">Not Reviewed</option>
                    <option value="Sent for Review">Sent for Review</option>
                    <option value="Attorney Approved">Attorney Approved</option>
                    <option value="Finch Rejection">Finch Rejection</option>
                    <option value="Attorney Rejection">
                      Attorney Rejection
                    </option>
                  </Select>
                  {/* Rejection Reason Field */}
                  {(statuses.attorneyDecision === 'Finch Rejection' ||
                    statuses.attorneyDecision === 'Attorney Rejection') && (
                    <div className="mt-2">
                      <Label className="text-xs font-medium text-gray-700 mb-0.5">
                        Rejection Reason
                      </Label>
                      <Textarea
                        value={statuses.rejectionReason || ''}
                        onChange={(event) =>
                          handleRejectionReasonChange(event.target.value)
                        }
                        placeholder="Enter the reason for rejecting this intake..."
                        className="w-full text-xs"
                        rows={2}
                      />
                    </div>
                  )}
                </Field>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusTrackingPanel;
